import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Form,
  Input,
  InputNumber,
  Space,
  Spin,
  Switch,
  Typography,
  Divider,
  message
} from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import api from '../utils/api'
import Hint from './common/Hint'

const { Text, Title } = Typography

const ItemRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  flex-wrap: wrap;
  margin-bottom: 8px;

  .item-name {
    flex: 1 1 200px;
  }
  .item-desc {
    flex: 2 1 240px;
  }
`

/**
 * 紅利點數設定（優惠模組頁的分頁）。
 *
 * 結帳與儲值會依這裡的比例自動送點；儲值金、紅利金付的部分不計點。
 * 手動加點與兌換只能選這裡設定的項目，店員不能自己打數字。
 * 沒有 points:settings 權限時整頁唯讀。
 */
const PointsSettings = ({ businessId, canEdit }) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const enabled = Form.useWatch('enabled', form)

  const load = useCallback(async () => {
    if (!businessId) return
    setLoading(true)
    try {
      const res = await api.get(`/api/businesses/${businessId}/points/settings`)
      form.setFieldsValue(res.data?.data || {})
    } catch (e) {
      message.error(e.response?.data?.message || '讀取紅利點數設定失敗')
    } finally {
      setLoading(false)
    }
  }, [businessId, form])

  useEffect(() => {
    load()
  }, [load])

  const handleSave = async (values) => {
    setSaving(true)
    try {
      const res = await api.put(
        `/api/businesses/${businessId}/points/settings`,
        values
      )
      form.setFieldsValue(res.data?.data || {})
      message.success(res.data?.message || '已儲存')
    } catch (e) {
      message.error(e.response?.data?.message || '儲存失敗')
    } finally {
      setSaving(false)
    }
  }

  const renderItems = (name, { addLabel, withDescription }) => (
    <Form.List name={name}>
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name: idx }) => (
            <ItemRow key={key}>
              {/* 保留既有項目的 _id：顧客頁用 _id 選項目，存檔換新 id 會讓開著的頁面送錯 */}
              <Form.Item name={[idx, '_id']} hidden noStyle>
                <Input />
              </Form.Item>
              <Form.Item
                name={[idx, 'name']}
                className='item-name'
                style={{ marginBottom: 0 }}
                rules={[{ required: true, message: '請輸入名稱' }]}
              >
                <Input placeholder='名稱' disabled={!canEdit} />
              </Form.Item>
              <Form.Item
                name={[idx, 'points']}
                style={{ marginBottom: 0 }}
                rules={[{ required: true, message: '請輸入點數' }]}
              >
                <InputNumber
                  min={1}
                  precision={0}
                  addonAfter='點'
                  style={{ width: 130 }}
                  disabled={!canEdit}
                />
              </Form.Item>
              {withDescription && (
                <Form.Item
                  name={[idx, 'description']}
                  className='item-desc'
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    placeholder='說明（選填，例：圓奈導入、手技按摩擇一）'
                    disabled={!canEdit}
                  />
                </Form.Item>
              )}
              <Form.Item
                name={[idx, 'isEnabled']}
                valuePropName='checked'
                style={{ marginBottom: 0 }}
              >
                <Switch
                  checkedChildren='啟用'
                  unCheckedChildren='停用'
                  disabled={!canEdit}
                />
              </Form.Item>
              {canEdit && (
                <Button
                  type='text'
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => remove(idx)}
                />
              )}
            </ItemRow>
          ))}
          {canEdit && (
            <Button
              type='dashed'
              icon={<PlusOutlined />}
              onClick={() => add({ points: 1, isEnabled: true })}
            >
              {addLabel}
            </Button>
          )}
        </>
      )}
    </Form.List>
  )

  return (
    <Spin spinning={loading}>
      <Form
        form={form}
        layout='vertical'
        onFinish={handleSave}
        initialValues={{
          enabled: false,
          spendAmountPerPoint: 1000,
          topupAmountPerPoint: 1000,
          earnRules: [],
          rewards: []
        }}
        style={{ maxWidth: 760 }}
      >
        {!canEdit && (
          <Hint tone='info' title='僅供檢視'>
            修改紅利點數設定需要「紅利點數設定」權限，請洽店主或店長。
          </Hint>
        )}

        <Form.Item name='enabled' label='啟用紅利點數' valuePropName='checked'>
          <Switch disabled={!canEdit} />
        </Form.Item>

        <Title level={5}>自動送點</Title>
        <Space wrap size='large'>
          <Form.Item
            name='spendAmountPerPoint'
            label='結帳消費'
            extra='填 0 表示消費不送點'
          >
            <InputNumber
              min={0}
              precision={0}
              addonBefore='每'
              addonAfter='元送 1 點'
              style={{ width: 240 }}
              disabled={!canEdit}
            />
          </Form.Item>
          <Form.Item
            name='topupAmountPerPoint'
            label='儲值'
            extra='依實收金額計算，不含贈送金額；填 0 表示儲值不送點'
          >
            <InputNumber
              min={0}
              precision={0}
              addonBefore='每'
              addonAfter='元送 1 點'
              style={{ width: 240 }}
              disabled={!canEdit}
            />
          </Form.Item>
        </Space>
        {enabled && (
          <Hint tone='info' title='送點規則'>
            結帳時用儲值金、紅利金付的部分不計點（儲值當下已經送過點）。
            不滿一點的零頭不計，例如每 1000 元 1 點時，消費 1999 元送 1 點。
            作廢結帳單或刪除儲值紀錄時，會自動扣回當初送的點數。
          </Hint>
        )}

        <Divider />
        <Title level={5}>手動加點項目</Title>
        <Text type='secondary' style={{ display: 'block', marginBottom: 12 }}>
          例：五星好評／打卡 1 點、介紹親友消費 3 點。店員在顧客頁加點時只能選這些項目。
        </Text>
        {renderItems('earnRules', { addLabel: '新增加點項目' })}

        <Divider />
        <Title level={5}>兌換項目</Title>
        <Text type='secondary' style={{ display: 'block', marginBottom: 12 }}>
          例：滿 20 點兌換經典管理課程擇一、滿 30 點兌換進階管理課程擇一。
        </Text>
        {renderItems('rewards', {
          addLabel: '新增兌換項目',
          withDescription: true
        })}

        {canEdit && (
          <Form.Item style={{ marginTop: 24 }}>
            <Button type='primary' htmlType='submit' loading={saving}>
              儲存設定
            </Button>
          </Form.Item>
        )}
      </Form>
    </Spin>
  )
}

export default PointsSettings
